import { FaUserCheck, FaMoneyBillWave, FaBriefcase } from "react-icons/fa";
import { useInView } from "../hooks/useInView";
import { useState } from "react";

const LoanEligibility = () => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const criteria = [
    {
      icon: <FaUserCheck />,
      title: "Age Criteria",
      value: "21 to 55 years",
      color: "from-[#FF6B6B] to-[#C92A2A]",
      bgGradient: "from-[#FFE3E3] to-[#FFC9C9]",
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Income Requirements",
      value: "≥ ₹40,000",
      color: "from-[#51CF66] to-[#2F9E44]",
      bgGradient: "from-[#D3F9D8] to-[#B2F2BB]",
    },
    {
      icon: <FaBriefcase />,
      title: "Employment Type",
      value: "Salaried Professional",
      color: "from-[#339AF0] to-[#1971C2]",
      bgGradient: "from-[#D0EBFF] to-[#A5D8FF]",
    },
  ];

  return (
    <section id="loan-eligibility" ref={ref} className="w-full py-16 md:py-20 lg:py-24 bg-[#A9F9EE] relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-float"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#3D4A7C] rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-float" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Enhanced Header */}
        <div className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block mb-4">
            <span className="px-4 py-1.5 bg-gradient-to-r from-[#21A6B5] to-[#3D4A7C] text-white text-xs md:text-sm font-semibold font-['Inter'] rounded-full shadow-md">
              Check Your Eligibility
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-[60px] lg:leading-[60px] font-bold text-center text-[#323232] mb-4 font-['Inter'] drop-shadow-md">
            Loan Eligibility
          </h2>
          <p className="text-lg md:text-xl lg:text-[24px] lg:leading-[32px] font-normal text-center text-[#2C3E50] font-['Inter'] max-w-3xl mx-auto">
            Simple criteria to get you started on your loan journey
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {criteria.map((item, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`group relative bg-white rounded-3xl px-6 py-8 md:px-8 md:py-10 shadow-xl hover:shadow-2xl transition-all duration-700 cursor-pointer text-center overflow-hidden ${
                isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${
                hoveredIndex === index ? 'scale-105 -translate-y-2' : 'scale-100'
              }`}
              style={{ transitionDelay: `${(index + 2) * 100}ms` }}
            >
              {/* Animated Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${item.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Shine Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>

              {/* Floating Particles */}
              <div className="absolute inset-0 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full animate-float"></div>
                <div className="absolute top-20 right-10 w-3 h-3 bg-white rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
                <div className="absolute bottom-10 left-1/3 w-2 h-2 bg-white rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon Container with Gradient */}
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-3xl md:text-4xl mb-6 mx-auto shadow-xl transform transition-all duration-500 ${
                  hoveredIndex === index ? 'rotate-[360deg] scale-110' : 'rotate-0 scale-100'
                }`}>
                  <div className={`transition-all duration-500 ${
                    hoveredIndex === index ? 'scale-125 animate-pulse' : 'scale-100'
                  }`}>
                    {item.icon}
                  </div>
                </div>

                {/* Title */}
                <h3 className={`text-xl md:text-2xl font-semibold mb-3 font-['Inter'] transition-all duration-300 ${
                  hoveredIndex === index ? 'text-[#3D4A7C] scale-105' : 'text-[#2C3E50] scale-100'
                }`}>
                  {item.title}
                </h3>

                {/* Value */}
                <p className={`text-2xl md:text-3xl font-bold font-['Inter'] transition-all duration-300 bg-gradient-to-r ${item.color} bg-clip-text ${
                  hoveredIndex === index ? 'text-transparent scale-110' : 'text-[#3D4A7C] scale-100'
                }`}>
                  {item.value}
                </p>

                {/* Animated Border */}
                <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 bg-gradient-to-r ${item.color} transition-all duration-500 rounded-full ${
                  hoveredIndex === index ? 'w-3/4' : 'w-0'
                }`}></div>
              </div>

              {/* Corner Decorations */}
              <div className={`absolute top-0 left-0 w-16 h-16 bg-[#21A6B5] opacity-10 rounded-br-full transition-all duration-500 ${
                hoveredIndex === index ? 'scale-150' : 'scale-100'
              }`}></div>
              <div className={`absolute bottom-0 right-0 w-16 h-16 bg-[#21A6B5] opacity-10 rounded-tl-full transition-all duration-500 ${
                hoveredIndex === index ? 'scale-150' : 'scale-100'
              }`}></div>
            </div>
          ))}
        </div>

        {/* Bottom Info */}
        <div className={`text-center mt-10 md:mt-14 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`} style={{ transitionDelay: '500ms' }}>
          <p className="text-base md:text-lg text-[#2C3E50] font-['Inter']">
            Meet these simple criteria and get instant approval! 🎉
          </p>
        </div>
      </div>
    </section>
  );
};

export default LoanEligibility;
